import type { CompositionSection } from '@medplum/fhirtypes'
import { systemFromCodeableConcept } from '#src/codeableConcept.ts'
import { ASSESSMENT_SECTION_VALIDATED_TOOL } from '#src/codeSystems.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedLibrary } from '#src/models/library.ts'
import { generateUUID, now } from '#src/utils.ts'

export interface LibrarySection extends CompositionSection {
  id: string
}

/**
 * FHIR rejects sections with empty/whitespace `text.div`, so the builder
 * filters them out. Validated-tool sections are exempt — their `text.div` is
 * populated on creation and rendered through `ValidatedToolSection`. Shared
 * with `TemplatePage` so the editor's warning matches what the builder drops.
 */
export function isDroppableEmptySection(section: LibrarySection): boolean {
  if (
    systemFromCodeableConcept(section.code) ===
    ASSESSMENT_SECTION_VALIDATED_TOOL
  ) {
    return false
  }
  return (section.text?.div ?? '').trim() === ''
}

export class CompositionLibraryEncoded extends ExtendedLibrary {
  protected getSections<T extends LibrarySection>(): T[] {
    const content = this.resource.content
    return (
      content?.map((item, idx) => {
        invariant(
          item.data,
          `${this.reference.reference} content must have data`
        )
        const decodedString = base64Decode(item.data)
        invariant(
          item.contentType === 'application/json',
          `${this.reference.reference} content must be in JSON format`
        )
        const parsedJSON = JSON.parse(decodedString)
        const section = parsedJSON as T
        if (!section.id) {
          console.error(
            `${this.reference.reference} has a section without an id. Index: ${idx}`
          )
        }
        return section
      }) ?? []
    )
  }

  protected setSections<T extends LibrarySection>(sections: T[]): void {
    this.resource.content = sections.map((section) => ({
      contentType: 'application/json',
      data: base64Encode(
        JSON.stringify({ ...section, id: section.id ?? generateUUID() })
      ),
      creation: now(),
    }))
  }
}
